import React from 'react'
import PropTypes from 'prop-types'

import tinymce from 'tinymce/tinymce'
import 'tinymce/icons/default'
import './skins/ui/oxide/skin.css'
import 'tinymce/themes/silver'

// Any plugins you want to use has to be imported
import 'tinymce/plugins/paste'
import 'tinymce/plugins/link'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/code'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/template'
import 'tinymce/plugins/image'
import 'tinymce/plugins/link'
import 'tinymce/plugins/media'
import 'tinymce/plugins/print'
import 'tinymce/plugins/importcss'
import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/visualchars'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/codesample'
import 'tinymce/plugins/table'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/hr'
import 'tinymce/plugins/pagebreak'
import 'tinymce/plugins/nonbreaking'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/toc'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/imagetools'
import 'tinymce/plugins/textpattern'
import 'tinymce/plugins/noneditable'
import 'tinymce/plugins/help'
import 'tinymce/plugins/quickbars'

import './skins/content/dark/content.min.css'
import { Editor } from '@tinymce/tinymce-react'

const CustomEditor = ({
  height,
  value,
  onChange,
  label,
  templates,
  key,
  setTemplateId,
  clearValue,
  addAttachmentClick,
  disableAttachmentButton,
}) => {
  const editorRef = React.useRef(null)
  // Tracked via the editor's own focus/blur events rather than editor.hasFocus():
  // hasFocus() proved unreliable at effect time inside the iframe, so the value
  // sync below fired mid-typing and reset the caret (input came out reversed).
  const isFocusedRef = React.useRef(false)
  // Captured ONCE. `initialValue` must never change per render: tinymce-react@3
  // calls editor.setContent(initialValue) — with no caret restore — whenever the
  // initialValue prop differs from the previous one. Feeding the live `value`
  // (which changes every keystroke) into it reset the caret on every character
  // and reversed the input. External value changes are applied by the effect below.
  const initialValueRef = React.useRef(value)

  React.useEffect(() => {
    tinymce.init({
      selector: 'textarea',
      skin: './skins/oxide',
    })
  }, [])

  // Push EXTERNAL value changes (async load of an existing note/template, a
  // programmatic clear) into the editor — but NEVER while the user is in it.
  // tinymce-react@3 controlled-`value` mode re-set the content on every render,
  // and React's async state lagged fast typing, so it kept resetting mid-keystroke
  // and moved the cursor (typing "test" produced "tste"/reversed). Using
  // initialValue + this focus-guarded sync keeps outside updates working without
  // ever touching live input.
  React.useEffect(() => {
    const editor = editorRef.current
    if (!editor || isFocusedRef.current) return
    const incoming = value || ''
    if (incoming !== editor.getContent()) {
      editor.setContent(incoming)
    }
  }, [value])

  const getToolbar = () => {
    const baseToolbar = `preview fontselect fontsizeselect formatselect | bold italic underline strikethrough forecolor backcolor | link |
      alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | removeformat | code | template`

    return disableAttachmentButton ? baseToolbar : `${baseToolbar} | addAttachmentButton`
  }

  const handleChange = (content) => {
    onChange({
      htmlValue: content,
      textValue: content.replace(/<[^>]*>?/gm, ''),
    })
  }

  // Use key to force re-render when disableAttachmentButton changes
  const editorKey = `editor-${key || 'default'}-${disableAttachmentButton ? 'noattach' : 'attach'}`

  return (
    <>
      {label && <label>{label}</label>}
      <Editor
        key={editorKey}
        onInit={(evt, editor) => {
          editorRef.current = editor
          editor.on('focus', () => {
            isFocusedRef.current = true
          })
          editor.on('blur', () => {
            isFocusedRef.current = false
          })
        }}
        init={{
          toolbar1: getToolbar(),
          plugins:
            'print preview paste importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons',
          height: height || 300,
          templates: templates || [],
          setup: (editor) => {
            editor.ui.registry.addButton('addAttachmentButton', {
              text: 'Add Attachment',
              onAction: () => {
                if (addAttachmentClick) {
                  addAttachmentClick()
                }
              },
            })
          },
        }}
        onBeforeSetContent={(e) => {
          if (e.selection && !e.set) {
            const divSelect = document.querySelector('.tox-selectfield')
            const select = divSelect && divSelect.getElementsByTagName('select')[0]
            if (select) {
              clearValue()
              templates.forEach((temp) => {
                if (temp.title === select.value) {
                  setTemplateId(temp)
                }
              })
            }
          }
        }}
        onEditorChange={handleChange}
        initialValue={initialValueRef.current}
      />
    </>
  )
}

CustomEditor.propTypes = {
  height: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  customStyle: PropTypes.object.isRequired,
}
CustomEditor.defaultProps = {
  height: '200px',
}

export default CustomEditor
